import React, { SyntheticEvent, useState } from "react";
import { Box, Skeleton, Tab, Tabs, Typography } from "@mui/material";
import { AffiliateContainer } from "./styles";
import AffiliateDashboard from "./AffiliateDashboard";
import AffiliateSalesHistory from "./AffiliateSalesHistory";
import AffiliateTransfers from "./AffiliateTransfers";
import ButtonComponent from "../../../Components/ButtonComponent";
import CreateAffiliateModal from "./CreateAffiliateModal";
import {
	useCreateAffiliateMutation,
	useGetUserAffiliateQuery
} from "../../../store/services/affiliate/affiliate.service";
import { AffiliateUser, AffiliateCreateDTO, IPayOutType } from "../../../store/services/affiliate/dto/affiliate.dto";
import { notifyError } from "../../../utils/NotifyError";
import { IError } from "../../../utils/getErrorMessage";
import { useAppSelector } from "../../../store";
import { getFullAffiliateState } from "../../../store/slices/affiliate/affiliate.slice";

const Affiliate = () => {
	const [tab, setTab] = useState(0);
	const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
	const { affiliate } = useAppSelector(getFullAffiliateState);
	const { isLoading: isFetchingAffiliate } = useGetUserAffiliateQuery(null);
	const [callCreateAffiliate, { isLoading: isCreatingAffiliate }] = useCreateAffiliateMutation();

	const handleTabChange = (_event: SyntheticEvent, newValue: number) => {
		setTab(newValue);
	};

	const handleCreate = (payload: Partial<AffiliateUser>) => {
		const body: AffiliateCreateDTO = {
			business_name: payload.businessName || "",
			holder_name: payload.payoutMethod?.holderName || "",
			payout_method: payload.payoutMethod?.type === IPayOutType.BANK ? 1 : 2
		};
		if (payload.payoutMethod?.type === IPayOutType.BANK) {
			body["payout_bank_name"] = payload.payoutMethod.bankName;
			body["payout_bank_account_number"] = payload.payoutMethod.accountNumber;
		} else if (payload.payoutMethod?.type === IPayOutType.WALLET) {
			body["payout_wallet_number"] = payload.payoutMethod.walletMobileNumber;
		}

		callCreateAffiliate(body).then(({ data, error }) => {
			if (data) {
				console.log(data);
				setIsCreateModalOpen(false);
			}
			if (error) {
				notifyError(error as IError);
				setIsCreateModalOpen(false);
			}
		});
	};

	if (isFetchingAffiliate) {
		return (
			<AffiliateContainer>
				<Skeleton variant={"rectangular"} height={48} sx={{ mb: 3 }} />
				<Skeleton variant={"rectangular"} height={200} />
			</AffiliateContainer>
		);
	}

	if (!affiliate) {
		return (
			<AffiliateContainer>
				<Box display={"flex"} flexDirection={"column"} alignItems={"center"} textAlign={"center"} gap={1.5} py={8}>
					<ButtonComponent
						variant={"contained"}
						color={"primary"}
						loading={false}
						onClick={() => setIsCreateModalOpen(true)}
						text={"Become an Affiliate"}
					/>
					<Typography variant={"body2"} color={"textSecondary"} maxWidth={420}>
						Join the affiliate program to create a unique shareable coupon and start earning commission on sales made
						through it.
					</Typography>
				</Box>
				<CreateAffiliateModal
					open={isCreateModalOpen}
					onClose={() => setIsCreateModalOpen(false)}
					onCreate={handleCreate}
					isCreatingAffiliate={isCreatingAffiliate}
				/>
			</AffiliateContainer>
		);
	}

	return (
		<AffiliateContainer>
			<Tabs value={tab} onChange={handleTabChange} variant={"fullWidth"} className={"affiliate-tabs"}>
				<Tab label={"Dashboard"} />
				<Tab label={"Sales History"} />
				<Tab label={"Transfers"} />
			</Tabs>
			<Box mt={3}>
				{tab === 0 && <AffiliateDashboard />}
				{tab === 1 && <AffiliateSalesHistory />}
				{tab === 2 && <AffiliateTransfers payoutMethod={affiliate.payoutMethod} />}
			</Box>
		</AffiliateContainer>
	);
};

export default Affiliate;
